import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaEye, FaEyeSlash, FaUser } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { OtpInput } from "reactjs-otp-input";
import * as Yup from "yup";
import logo from "../../../images/logo.svg";
import { getApiCaller } from "../../../libs/apiCaller";
import PasswordChangeSuccessModal from "../../modal/PasswordChangeSuccessModal";

const ForgotPassword = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [otpSendLoading, setOtpSendLoading] = useState(false);
  const emailAddress = "reebukhealth@gmail.com";
  const [userName, setUserName] = useState();
  const [showSection, setShowSection] = useState(1);
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(203); // 3 minutes 23 seconds
  const [isDisabled, setIsDisabled] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setIsDisabled(false); // Enable button when time reaches 0
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleOTPSent = async () => {
    try {
      const endPoint = `admin_send_otp?email=${emailAddress}&user_name=${userName}`;
      setOtpSendLoading(true);
      const response = await getApiCaller(endPoint);
      if (response?.data?.response_code === 200) {
        toast.success(response?.data?.response_message);
        setShowSection(2);
      } else {
        toast.error(
          response?.data?.response_message !== ""
            ? response?.data?.response_message
            : response?.message
        );
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setOtpSendLoading(false);
    }
  };

  // Validation Schema
  const validationSchema = Yup.object().shape({
    otp: Yup.string()
      .length(6, "OTP must be 6 digits")
      .required("OTP is required"),
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_^])[A-Za-z\d@$!%*?&_^]{8,}$/,
        "Password must have at least 1 uppercase, 1 lowercase, 1 number, and 1 special character"
      ),
    confirmPassword: Yup.string()
      .required("Confirm password is required")
      .oneOf(
        [Yup.ref("newPassword")],
        "New password and confirm password do not match"
      ),
  });

  // useFormik Hook
  const formik = useFormik({
    initialValues: { otp: "", newPassword: "", confirmPassword: "" },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const endPoint = `admin_verify_otp?otp=${otp}&user_name=${userName}&new_password=${values.newPassword}`;
        setOtpSendLoading(true);
        const response = await getApiCaller(endPoint);
        if (response?.data?.response_code === 200) {
          toast.success(response?.data?.response_message);
          formik.resetForm();
          setOtp("");
          setUserName("");
          setShowSection(1);
          navigate("/auth/login");
        } else {
          toast.error(
            response?.data?.response_message !== ""
              ? response?.data?.response_message
              : response?.message
          );
        }
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setOtpSendLoading(false);
      }
    },
  });

  return (
    <>
      <div className="h-screen grid grid-cols-1 md:grid-cols-2">
        <div className="bg-[#2841840D] hidden md:block">
          <div className="flex h-screen items-center justify-center">
            <p className="text-2xl">Reebuk Health</p>
          </div>
        </div>
        <div className="flex items-center justify-center">
          {showSection === 1 && (
            <div className="p-4 md:p-8 lg:p-12 w-full">
              <img src={logo} alt="logo-image" />
              <div className="my-6">
                <p className="text-2xl font-semibold text-[#284184]">
                  Forgot Password{" "}
                </p>
                <p className="text-[#8B8D98]">
                  Enter your registered email address. we’ll send you a code to
                  reset your password.
                </p>
              </div>
              <div>
                <label className="mb-2.5 block font-medium">User Name</label>
                <div className="relative">
                  <span className="absolute top-1/2 left-4 -translate-y-1/2 cursor-pointe">
                    <FaUser className="text-2xl" />
                  </span>
                  <input
                    type="text"
                    placeholder="Enter user name"
                    className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-12 pr-3 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    onChange={(e) => setUserName(e.target.value)}
                    value={userName}
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full mt-10 mb-5 bg-[#284184] text-white py-2 rounded-xl disabled:cursor-not-allowed"
                onClick={() => {
                  handleOTPSent();
                }}
                disabled={userName !== "Oct_2023_Info" || otpSendLoading}
                // disabled={otpSendLoading}
              >
                {otpSendLoading ? "Loading..." : "Send code"}
              </button>
              <Link
                to={"/auth/login"}
                className="w-full rounded-xl hover:underline"
              >
                <span className="flex justify-center items-center gap-2">
                  <FaChevronLeft />
                  Back To Log In
                </span>{" "}
              </Link>
            </div>
          )}
          {showSection === 2 && (
            <div className="p-4 md:p-8 lg:p-16 w-full">
              <img src={logo} alt="logo-image" />
              <div className="my-6">
                <p className="text-2xl font-semibold text-[#284184]">
                  Reset your password
                </p>
                <p className="text-[#8B8D98]">
                  At least 8 characters, with uppercase, lowercase and special
                  character letters
                </p>
              </div>
              <form onSubmit={formik.handleSubmit}>
                {/* OTP Input */}
                <div className="mb-5">
                  <OtpInput
                    value={formik.values.otp}
                    onChange={(value) => {
                      setOtp(value);
                      formik.setFieldValue("otp", value);
                    }}
                    numInputs={6}
                    isInputNum
                    containerStyle={{ gap: "1rem" }}
                    inputStyle="!w-12 !h-12 text-xl text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                  />
                  {formik.touched.otp && formik.errors.otp && (
                    <div className="text-red-500 text-sm mt-2">
                      {formik.errors.otp}
                    </div>
                  )}
                </div>

                {/* Resend Code Button */}
                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    className={`${
                      isDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-blue-600 hover:text-blue-700"
                    }`}
                    disabled={isDisabled}
                    onClick={() => {
                      setTimeLeft(203);
                      setIsDisabled(true);
                      handleOTPSent();
                      setOtp("");
                      formik.setFieldValue("otp", "");
                    }}
                  >
                    Resend code
                  </button>
                  <div className="text-gray-500">{formatTime(timeLeft)}</div>
                </div>

                {otp.length === 6 && (
                  <>
                    {/* New Password */}
                    <div className="my-4">
                      <label className="mb-2.5 block font-medium">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          name="newPassword"
                          placeholder="Enter new password"
                          className="w-full rounded-lg border border-gray-300 bg-transparent py-4 pl-6 pr-10 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          value={formik.values.newPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <button
                          type="button"
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer"
                        >
                          {passwordVisible ? (
                            <FaEye className="text-xl" />
                          ) : (
                            <FaEyeSlash className="text-xl" />
                          )}
                        </button>
                      </div>
                      {formik.touched.newPassword &&
                        formik.errors.newPassword && (
                          <div className="text-red-500 text-sm mt-2">
                            {formik.errors.newPassword}
                          </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className="mt-3">
                      <label className="mb-2.5 block font-medium">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={confirmPasswordVisible ? "text" : "password"}
                          name="confirmPassword"
                          placeholder="Enter Confirm password"
                          className="w-full rounded-lg border border-gray-300 bg-transparent py-4 pl-6 pr-10 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          value={formik.values.confirmPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setConfirmPasswordVisible(!confirmPasswordVisible)
                          }
                          className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer"
                        >
                          {confirmPasswordVisible ? (
                            <FaEye className="text-xl" />
                          ) : (
                            <FaEyeSlash className="text-xl" />
                          )}
                        </button>
                      </div>
                      {formik.touched.confirmPassword &&
                        formik.errors.confirmPassword && (
                          <div className="text-red-500 text-sm mt-2">
                            {formik.errors.confirmPassword}
                          </div>
                        )}
                    </div>
                  </>
                )}

                {/* Reset Password Button */}
                <button
                  type="submit"
                  className="w-full mt-10 mb-5 bg-[#284184] text-white py-2 font-bold rounded-xl disabled:cursor-not-allowed"
                  disabled={formik.values.otp.length < 6 || otpSendLoading}
                >
                  {otpSendLoading ? "Loading..." : "Reset password"}
                </button>
              </form>
              <Link
                to={"/auth/login"}
                className="w-full rounded-xl hover:underline"
              >
                <span className="flex justify-center items-center gap-2">
                  <FaChevronLeft />
                  Back To Log In
                </span>{" "}
              </Link>
            </div>
          )}
        </div>
      </div>
      <PasswordChangeSuccessModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default ForgotPassword;
